/**
 * Study completion-moment motion (umbrella study-motion-feedback-prd.md §2, D-SMF-04).
 *
 * Imperative effects for success handlers: fly, burst, pop, confetti, grow,
 * stamp and nudge. Every effect is decorative: it is aria-hidden, ignores the
 * pointer, never moves focus, cleans up its elements and never throws.
 * Reduced motion, a missing element or a browser without the Web Animations API
 * skip the effect; callers keep their existing text feedback.
 */

import { prefersReducedMotion } from '@/components/celebration/reducedMotion';

export { prefersReducedMotion };

export type MotionTarget = Element | null | undefined;
export type GhostKind = 'sheet' | 'card' | 'star';

const FLY_MS = 1100;
const POP_MS = 450;
const BURST_MS = 650;
const CONFETTI_MS = 1700;
const GROW_MS = 500;
const STAMP_MS = 500;
const NUDGE_MS = 320;
const ARC_STEPS = 14;
const ARC_LIFT_PX = 90;
const MAX_LIVE_ELEMENTS = 60;
const LAYER_CLASS = 'pointer-events-none fixed z-[60]';

const GHOST_SIZE: Record<GhostKind, { w: number; h: number }> = {
  sheet: { w: 56, h: 72 },
  card: { w: 120, h: 40 },
  star: { w: 28, h: 28 },
};
const BURST_TONES = ['bg-brand-sunshine', 'bg-brand-coral', 'bg-brand-sky', 'bg-brand-mint'];
const CONFETTI_TONES = [...BURST_TONES, 'bg-brand-bubblegum'];
const SHEET_LINE_WIDTHS = [90, 70, 82, 60, 76];

let live = 0;

/** True when an effect may run on this element. */
function canAnimate(element: MotionTarget): element is HTMLElement {
  return (
    !!element &&
    element instanceof HTMLElement &&
    typeof element.animate === 'function' &&
    !prefersReducedMotion()
  );
}

/** First element that is actually on screen (several navs exist; only one is visible). */
export function visibleElement(selectors: string[], root: ParentNode = document): HTMLElement | null {
  for (const selector of selectors) {
    for (const element of Array.from(root.querySelectorAll<HTMLElement>(selector))) {
      const rect = element.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) return element;
    }
  }
  return null;
}

/** The visible Study nav link (`Home`, `Materials`) or the More button; Kids dock Study as fallback. */
export function studyNavTarget(label: 'Home' | 'Materials' | 'More'): HTMLElement | null {
  const navs = ['nav[aria-label="Study navigation"]', 'nav[aria-label="Study mobile navigation"]'];
  const own =
    label === 'More'
      ? navs.map((nav) => `${nav} button[aria-haspopup="menu"]`)
      : navs.map((nav) => `${nav} a[aria-label="${label}"]`);
  return visibleElement([...own, '[data-testid="learn-navigation-dock"] a[href="/learn/study"]']);
}

function layer(className: string, rect: { left: number; top: number; width: number; height: number }) {
  const node = document.createElement('div');
  node.setAttribute('aria-hidden', 'true');
  node.dataset.studyMotion = '';
  node.className = `${LAYER_CLASS} ${className}`;
  Object.assign(node.style, {
    left: `${rect.left}px`,
    top: `${rect.top}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
  });
  document.body.appendChild(node);
  live += 1;
  return node;
}

function release(node: HTMLElement) {
  node.remove();
  live = Math.max(0, live - 1);
}

function centre(element: Element) {
  const r = element.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.top + Math.min(r.height, 240) / 2 };
}

function ghost(kind: GhostKind, x: number, y: number, label?: string) {
  const { w, h } = GHOST_SIZE[kind];
  const rect = { left: x - w / 2, top: y - h / 2, width: w, height: h };
  if (kind === 'sheet') {
    const node = layer(
      'flex flex-col gap-1.5 rounded-xl border border-hairline bg-canvas-pure px-2.5 py-3 shadow-card-soft',
      rect,
    );
    for (const width of SHEET_LINE_WIDTHS) {
      const line = document.createElement('span');
      line.className = 'block h-1 rounded-full bg-hairline';
      line.style.width = `${width}%`;
      node.appendChild(line);
    }
    return node;
  }
  if (kind === 'card') {
    const node = layer(
      'flex items-center justify-center truncate rounded-full border border-hairline bg-canvas-pure px-3 text-xs font-extrabold text-ink shadow-card-soft',
      rect,
    );
    node.textContent = label ?? '';
    return node;
  }
  const node = layer('flex items-center justify-center rounded-full bg-brand-sunshine text-sm text-ink shadow-card-soft', rect);
  node.textContent = '★';
  return node;
}

/** Keyframes along a quadratic arc from (0,0) to (dx,dy). */
export function arcKeyframes(dx: number, dy: number, endScale = 0.3): Keyframe[] {
  return Array.from({ length: ARC_STEPS + 1 }, (_, i) => {
    const t = i / ARC_STEPS;
    return {
      transform: `translate(${dx * t}px, ${dy * t - ARC_LIFT_PX * 4 * t * (1 - t)}px) scale(${1 - (1 - endScale) * t}) rotate(${-12 * t}deg)`,
      opacity: t > 0.85 ? Math.max(0, 1 - (t - 0.85) / 0.15) : 1,
      offset: t,
    };
  });
}

async function settle(animation: Animation): Promise<boolean> {
  try {
    await animation.finished;
    return true;
  } catch {
    return false;
  }
}

/** `pop`: the element scales 1 → 1.12 → 1. */
export async function pop(target: MotionTarget): Promise<boolean> {
  if (!canAnimate(target)) return false;
  return settle(
    target.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.12)' }, { transform: 'scale(1)' }], {
      duration: POP_MS,
      easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
    }),
  );
}

/** `fly`: a ghost leaves `source`, flies along an arc into `target`, then the target pops. */
export async function fly(
  source: MotionTarget,
  target: MotionTarget,
  options: { ghost?: GhostKind; label?: string; delayMs?: number } = {},
): Promise<boolean> {
  if (!canAnimate(source) || !canAnimate(target) || live >= MAX_LIVE_ELEMENTS) return false;
  const from = centre(source);
  const to = centre(target);
  const node = ghost(options.ghost ?? 'sheet', from.x, from.y, options.label);
  try {
    const ok = await settle(
      node.animate(arcKeyframes(to.x - from.x, to.y - from.y), {
        duration: FLY_MS,
        delay: options.delayMs ?? 0,
        easing: 'cubic-bezier(0.45, 0, 0.2, 1)',
        fill: 'both',
      }),
    );
    if (!ok) return false;
    await pop(target);
    return true;
  } finally {
    release(node);
  }
}

/** `burst`: small particles spray out from the element and fade. */
export async function burst(source: MotionTarget, count = 10): Promise<boolean> {
  if (!canAnimate(source)) return false;
  const { x, y } = centre(source);
  const particles = Array.from({ length: Math.min(count, MAX_LIVE_ELEMENTS - live) }, (_, i) => {
    const node = layer(`rounded-full ${BURST_TONES[i % BURST_TONES.length]}`, {
      left: x - 4,
      top: y - 4,
      width: 8,
      height: 8,
    });
    const angle = (i / count) * Math.PI * 2;
    const distance = 36 + (i % 3) * 10;
    const animation = node.animate(
      [
        { transform: 'translate(0, 0) scale(1)', opacity: 1 },
        {
          transform: `translate(${Math.cos(angle) * distance}px, ${Math.sin(angle) * distance}px) scale(0.4)`,
          opacity: 0,
        },
      ],
      { duration: BURST_MS, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'forwards' },
    );
    return { node, animation };
  });
  const results = await Promise.all(particles.map(({ animation }) => settle(animation)));
  particles.forEach(({ node }) => release(node));
  return results.every(Boolean);
}

/** `confetti`: paper pieces fall across the top of the viewport. */
export async function confetti(count = 48): Promise<boolean> {
  if (prefersReducedMotion() || typeof document.body.animate !== 'function') return false;
  const width = window.innerWidth;
  const pieces = Array.from({ length: Math.min(count, MAX_LIVE_ELEMENTS - live) }, (_, i) => {
    const node = layer(`rounded-sm ${CONFETTI_TONES[i % CONFETTI_TONES.length]}`, {
      left: ((i * 97) % 100) * (width / 100),
      top: -16,
      width: 8,
      height: 12,
    });
    const drift = ((i % 7) - 3) * 18;
    const fall = window.innerHeight * (0.55 + (i % 5) * 0.08);
    return { node, animation: node.animate(
      [
        { transform: 'translate(0, 0) rotate(0deg)', opacity: 1 },
        { transform: `translate(${drift}px, ${fall}px) rotate(${(i % 2 ? 1 : -1) * 540}deg)`, opacity: 0 },
      ],
      { duration: CONFETTI_MS, delay: (i % 12) * 40, easing: 'cubic-bezier(0.25, 0.6, 0.4, 1)', fill: 'both' },
    ) };
  });
  const results = await Promise.all(pieces.map(({ animation }) => settle(animation)));
  pieces.forEach(({ node }) => release(node));
  return results.every(Boolean);
}

/** `grow`: the element scales out from `from`'s position. */
export async function grow(target: MotionTarget, from?: MotionTarget): Promise<boolean> {
  if (!canAnimate(target)) return false;
  let offset = { x: 0, y: 0 };
  if (from) {
    const a = centre(from);
    const b = centre(target);
    offset = { x: a.x - b.x, y: a.y - b.y };
  }
  return settle(
    target.animate(
      [
        { transform: `translate(${offset.x}px, ${offset.y}px) scale(0.3)`, opacity: 0 },
        { transform: 'translate(0, 0) scale(1)', opacity: 1 },
      ],
      { duration: GROW_MS, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)', composite: 'add' },
    ),
  );
}

/** `stamp`: a small label lands on the target from a larger size. */
export async function stamp(target: MotionTarget, label: string): Promise<boolean> {
  if (!canAnimate(target)) return false;
  const { x, y } = centre(target);
  const node = layer(
    'flex items-center justify-center rounded-full border-2 border-brand-coral bg-canvas-pure px-2 text-xs font-extrabold text-brand-coral',
    { left: x - 34, top: y - 30, width: 68, height: 24 },
  );
  node.textContent = label;
  try {
    return await settle(
      node.animate(
        [
          { transform: 'scale(2.2) rotate(-12deg)', opacity: 0 },
          { transform: 'scale(1) rotate(-6deg)', opacity: 1, offset: 0.55 },
          { transform: 'scale(1) rotate(-6deg)', opacity: 0 },
        ],
        { duration: STAMP_MS * 2, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'forwards' },
      ),
    );
  } finally {
    release(node);
  }
}

/** `nudge`: a gentle sideways shake; never a punishment (D-SMF-03). */
export async function nudge(target: MotionTarget): Promise<boolean> {
  if (!canAnimate(target)) return false;
  return settle(
    target.animate(
      [
        { transform: 'translateX(0)' },
        { transform: 'translateX(-4px)' },
        { transform: 'translateX(4px)' },
        { transform: 'translateX(-2px)' },
        { transform: 'translateX(0)' },
      ],
      { duration: NUDGE_MS, easing: 'ease-in-out' },
    ),
  );
}
